import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, RefreshCw, Search, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZES = [25, 50, 100];

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString("tr-TR");
};

const DeviceFailedRecordsTable = ({
  API,
  deviceId,
  isDark,
  isActive,
  inputClass,
  labelClass,
}) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchRecords = useCallback(async () => {
    if (!deviceId || !isActive) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("device_id", String(deviceId));
      params.append("page", String(page));
      params.append("limit", String(limit));
      if (searchTerm) params.append("search", searchTerm);

      const res = await axios.get(`${API}/device-failed-records?${params}`, { timeout: 60000 });
      setRecords(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error("Failed records error:", error);
      setRecords([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [API, deviceId, isActive, page, limit, searchTerm]);

  useEffect(() => {
    if (isActive && deviceId) {
      fetchRecords();
    }
  }, [fetchRecords, isActive, deviceId]);

  useEffect(() => {
    setPage(1);
    setSearchTerm("");
    setSearchInput("");
  }, [deviceId]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";
  const cardClass = isDark
    ? "rounded-xl border border-slate-800 bg-slate-950/50 overflow-hidden"
    : "rounded-xl border border-gray-200 bg-gray-50/80 overflow-hidden";

  return (
    <div className="space-y-3">
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 pt-4`}>
        <div className="flex items-center gap-2">
          <VolumeX className={`w-4 h-4 ${isDark ? "text-red-400" : "text-red-500"}`} />
          <p className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-gray-800"}`}>
            Eksik Ses Kayıtları
          </p>
          <Badge variant="outline" className={isDark ? "border-red-500/30 text-red-300 font-mono" : "border-red-200 text-red-700 font-mono"}>
            {total.toLocaleString("tr-TR")}
          </Badge>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="ID, numara veya kişi ara..."
            className={`h-8 w-full sm:w-52 text-xs ${inputClass}`}
          />
          <Button type="submit" size="sm" className="h-8 px-2.5 shrink-0">
            <Search className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={fetchRecords} disabled={loading} className="h-8 w-8 p-0 shrink-0">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </form>
      </div>

      <div className={cardClass}>
        {loading ? (
          <div className={`flex items-center justify-center py-12 gap-2 ${mutedClass}`}>
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="text-sm">Yükleniyor…</span>
          </div>
        ) : records.length === 0 ? (
          <div className={`text-center py-12 text-sm ${mutedClass}`}>
            Bu cihaz için eksik ses kaydı bulunamadı.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className={isDark ? "border-slate-800 hover:bg-transparent" : ""}>
                  <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Call Log ID</TableHead>
                  <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Device ID</TableHead>
                  <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Telefon No</TableHead>
                  <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Kişi Adı</TableHead>
                  <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Çağrı Tarihi</TableHead>
                  <TableHead className={`whitespace-nowrap text-center ${isDark ? "text-slate-400" : ""}`}>Tarama</TableHead>
                  <TableHead className={`whitespace-nowrap min-w-[180px] ${isDark ? "text-slate-400" : ""}`}>Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((row) => (
                  <TableRow key={row.call_id} className={isDark ? "border-slate-800/50" : ""}>
                    <TableCell className={`font-mono ${isDark ? "text-slate-400" : "text-gray-500"}`}>{row.call_id}</TableCell>
                    <TableCell className={`font-mono ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{row.device_id}</TableCell>
                    <TableCell className={`font-mono ${isDark ? "text-amber-400" : "text-amber-600"}`}>{row.phone_number || "-"}</TableCell>
                    <TableCell className={isDark ? "text-slate-300" : ""}>{row.contact_name || "-"}</TableCell>
                    <TableCell className={`whitespace-nowrap ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                      {formatDateTime(row.call_date2)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={`font-mono text-[10px] px-1.5 ${isDark ? "border-slate-600 text-slate-400" : "border-gray-300 text-gray-600"}`}>
                        {row.tarama_sayisi}/{row.tarama_max}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] font-medium whitespace-normal text-left h-auto py-1 ${isDark ? "bg-red-950/70 text-red-300 hover:bg-red-950/70" : "bg-red-600 text-white hover:bg-red-600"}`}>
                        {row.status_label}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
            <span className={`text-xs ${labelClass}`}>
              {total.toLocaleString("tr-TR")} kayıt · Sayfa {page}/{totalPages}
            </span>
            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                className={`h-8 text-xs rounded-md px-2 ${isDark ? "bg-slate-900 border border-slate-700 text-white" : "bg-white border border-gray-300"}`}
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className={`h-8 w-8 p-0 ${isDark ? "border-slate-700" : ""}`}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)} className={`h-8 w-8 p-0 ${isDark ? "border-slate-700" : ""}`}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceFailedRecordsTable;
